import { REPLY_DID_NOT_ARRIVE_MESSAGE } from './betaAllowance';
import { STREAMING_REPLY_WAIT } from './streamingReplyDisplay';

export const REPLY_POLL_MS = 2000;

/** A remount with nothing in flight keeps asking this long, then try again. */
export const REPLY_RECOVER_MS = 30000;

/**
 * The server keeps one generation going this long after the browser leaves.
 * While that marker is set, try again would start a second charge.
 */
export const REPLY_FINISH_CAP_MS = 120000;

/**
 * An empty "Writing a reply…" with nothing in flight must not stay up past this.
 * A slow reply that already has words is not this case.
 * A fresh in-flight marker uses REPLY_FINISH_CAP_MS instead.
 */
export const REPLY_STALL_MS = REPLY_RECOVER_MS;

/** Marker stored when Get me started has no user message to point at. */
export const JUMP_START_IN_FLIGHT_ID = 'jump_start';

const WAIT_KEY = 'brainstormy.replyWait';

function messageAgeMs(createdAt, now) {
  if (!createdAt) return null;
  const stamp = new Date(createdAt).getTime();
  if (!Number.isFinite(stamp)) return null;
  return now - stamp;
}

export function rememberReplyWait(sessionId, action) {
  if (!sessionId) return;
  try {
    sessionStorage.setItem(WAIT_KEY, JSON.stringify({
      sessionId: String(sessionId),
      action: action || 'send',
      startedAt: new Date().toISOString(),
    }));
  } catch {
    /* the open page still has the bubble */
  }
}

export function readReplyWait(sessionId) {
  if (!sessionId) return null;
  try {
    const raw = JSON.parse(sessionStorage.getItem(WAIT_KEY) || 'null');
    if (!raw || String(raw.sessionId) !== String(sessionId)) return null;
    if (!raw.startedAt) return null;
    return raw;
  } catch {
    return null;
  }
}

export function forgetReplyWait(sessionId) {
  const current = readReplyWait(sessionId);
  if (!current) return;
  try {
    sessionStorage.removeItem(WAIT_KEY);
  } catch {
    /* the next load treats a missing flag as no wait */
  }
}

function gapForInFlight(messages, replyInFlight, now) {
  if (!replyInFlight?.message_id) return null;
  const started = replyInFlight.started_at;
  if (started && assistantLandedAfter(messages, started)) return null;
  if (started) {
    const age = messageAgeMs(started, now);
    if (age != null && age < 0) return null;
  }
  const opener = String(replyInFlight.message_id) === JUMP_START_IN_FLIGHT_ID;
  // The marker means this generation is still the one running. Try again
  // waits until the server clears it, including past the old 30s give-up.
  const gap = {
    kind: 'writing',
    message: STREAMING_REPLY_WAIT,
  };
  if (opener) gap.action = 'jump_start';
  return gap;
}

/** A 409 from send/retry while a reply is already generating. */
export function replyInFlightConflict(err) {
  if (!err || Number(err.status) !== 409) return false;
  if (err.code === 'reply_in_flight') return true;
  return /still being written/i.test(String(err.message || ''));
}

/** Matches the writer-row flag set in backend/api/chat.py on a muted send. */
export function heardWhileMuted(message) {
  return message?.metadata?.heard_while_muted === true;
}

/** The open tail is a line kept on purpose, with no reply owed. */
export function mutedSilenceTail(messages) {
  const list = Array.isArray(messages) ? messages : [];
  const last = list[list.length - 1];
  return Boolean(last && last.speaker === 'user' && !last.streaming && heardWhileMuted(last));
}

function assistantLandedAfter(messages, startedAt) {
  const start = new Date(startedAt).getTime();
  if (!Number.isFinite(start)) return false;
  return (messages || []).some((message) => {
    const who = String(message?.speaker || message?.role || '').toLowerCase();
    if (who === 'user' || message?.streaming) return false;
    if (!String(message?.content || '').trim()) return false;
    const stamp = new Date(message.created_at).getTime();
    return Number.isFinite(stamp) && stamp >= start;
  });
}

/**
 * A user message with nothing under it is either still being written
 * or it never arrived. A streaming bubble is not a gap: that reply is
 * already on this page. An empty wait does not stay open forever.
 */
export function replyGapForThread({
  messages,
  replyInFlight = null,
  sending = false,
  muted = false,
  pendingWait = null,
  now = Date.now(),
} = {}) {
  if (sending || muted) return null;
  const list = Array.isArray(messages) ? messages : [];
  // Dictation kept the line and promised no reply. A pending in-flight
  // claim and a remembered send-wait used to be read before this flag,
  // so either one painted "Writing a reply…" or the failed-reply line
  // under that tail. Those two do not count here. Get me started, or a
  // marker for another message, still does.
  let inflight = replyInFlight;
  let wait = pendingWait;
  if (mutedSilenceTail(list)) {
    const last = list[list.length - 1];
    const inflightId = inflight?.message_id ? String(inflight.message_id) : '';
    if (!inflightId || inflightId === 'pending' || inflightId === String(last.id || '')) {
      inflight = null;
    }
    if (!wait?.startedAt || (wait.action || 'send') === 'send') {
      wait = null;
    }
    if (!inflight?.message_id && !wait?.startedAt) return null;
  }
  const inflightGap = gapForInFlight(list, inflight, now);
  if (inflightGap) return inflightGap;
  if (wait?.startedAt && !assistantLandedAfter(list, wait.startedAt)) {
    const age = messageAgeMs(wait.startedAt, now);
    if (age != null && age >= 0 && age < REPLY_RECOVER_MS) {
      return { kind: 'writing', message: STREAMING_REPLY_WAIT, action: wait.action || 'send' };
    }
    if (age != null && age >= REPLY_RECOVER_MS) {
      return { kind: 'missing', message: REPLY_DID_NOT_ARRIVE_MESSAGE, action: wait.action || 'send' };
    }
  }
  if (!list.length) return null;
  const last = list[list.length - 1];
  if (!last || last.speaker !== 'user' || last.streaming) return null;
  // The idea that opened this chat is a writer line on purpose. Nothing
  // was asked to answer it, so it is not a reply that failed to arrive.
  if (heardWhileMuted(last) || last.metadata?.kind === 'nudge_seed') return null;
  const age = messageAgeMs(last.created_at, now);
  if (age != null && age >= 0 && age <= REPLY_RECOVER_MS) {
    return { kind: 'writing', message: STREAMING_REPLY_WAIT };
  }
  return { kind: 'missing', message: REPLY_DID_NOT_ARRIVE_MESSAGE };
}

/** Milliseconds left before an empty wait becomes the retry sentence. */
export function replyRecoverRemainingMs({
  messages,
  replyInFlight = null,
  pendingWait = null,
  now = Date.now(),
} = {}) {
  const list = Array.isArray(messages) ? messages : [];
  if (pendingWait?.startedAt && !assistantLandedAfter(list, pendingWait.startedAt)) {
    const age = messageAgeMs(pendingWait.startedAt, now);
    if (age == null) return null;
    return REPLY_RECOVER_MS - age;
  }
  if (replyInFlight?.started_at && !assistantLandedAfter(list, replyInFlight.started_at)) {
    const age = messageAgeMs(replyInFlight.started_at, now);
    if (age == null) return REPLY_FINISH_CAP_MS;
    if (age >= REPLY_FINISH_CAP_MS) return null;
    return REPLY_FINISH_CAP_MS - age;
  }
  const last = list[list.length - 1];
  if (!last || last.speaker !== 'user') return null;
  const age = messageAgeMs(last.created_at, now);
  if (age == null) return null;
  return REPLY_RECOVER_MS - age;
}

export function sendLocked({ sending = false, messages, gap } = {}) {
  if (sending) return true;
  if ((messages || []).some((message) => message?.streaming)) return true;
  return gap?.kind === 'writing';
}

/** An assistant row the open page did not already have. */
export function recoveredAssistant(localMessages, serverMessages) {
  const known = new Set(
    (localMessages || [])
      .filter((message) => (
        message?.id
        && !message.streaming
        && !String(message.id).startsWith('temp-')
        && !String(message.id).startsWith('streaming-')
      ))
      .map((message) => String(message.id)),
  );
  const server = Array.isArray(serverMessages) ? serverMessages : [];
  return [...server].reverse().find((message) => {
    const who = String(message?.speaker || message?.role || '').toLowerCase();
    if (who !== 'assistant' && who !== 'ai') return false;
    if (message?.streaming) return false;
    if (!String(message?.content || '').trim()) return false;
    return !known.has(String(message.id));
  }) || null;
}

export function withoutEmptyReplyWait(messages) {
  return (messages || []).filter((message) => !(
    message?.streaming && !String(message?.content || '').trim()
  ));
}
